import { Buffer } from 'buffer';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Switch,
  Tab,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import Input from '../tools/Input';
import Selector from '../tools/Selector';
import {
  READING_AI_TYPES,
  SHELF_READING_AI_TYPES,
  COLLECTOR_AI_TYPES,
  LAYOUT_ID_AI_TYPES,
  SHELFOUT_POSITION_SCOPES,
  SHELFOUT_POSITION_SOURCES,
} from '../tools/Canvas/utils';

const StyledSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.type === 'light' ? 'white' : 'primary',
  '& .MuiSlider-thumb': {
    color: theme.palette.type === 'light' ? 'white' : 'primary',
  },
  '& .MuiSlider-rail': {
    color: theme.palette.type === 'light' ? 'white' : 'primary',
  },
  '& .MuiSlider-track': {
    color: theme.palette.type === 'light' ? 'white' : 'primary',
  },
}));

const StyledMenu = styled(props => <Menu {...props} />)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor:
      theme.palette.type === 'light' ? '#1a242d' : theme.palette.paper.main,
    borderRadius: 3,
    marginTop: theme.spacing(1),
    minWidth: 300,
    color: theme.palette.mode === 'light' ? 'white' : 'black',
  },
}));

const OptionSwitch = ({ value, onChange, leftOption, rightOption, color }) => {
  const isRightSelected = value === rightOption.value;
  const optionSx = isSelected => ({
    flex: 1,
    fontWeight: isSelected ? 700 : 400,
    opacity: isSelected ? 1 : 0.6,
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body2" align="right" sx={optionSx(!isRightSelected)}>
        {leftOption.label}
      </Typography>
      <Switch
        color={color}
        checked={isRightSelected}
        onChange={event =>
          onChange(event.target.checked ? rightOption.value : leftOption.value)
        }
        inputProps={{
          'aria-label': `${leftOption.label} / ${rightOption.label}`,
        }}
      />
      <Typography variant="body2" sx={optionSx(isRightSelected)}>
        {rightOption.label}
      </Typography>
    </Box>
  );
};

export default function ToolMenu(props) {
  const { REACT_APP_USE_BIFROST } = process.env;
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    formState,
    setFormState,
    format,
    setFormat,
    showDetection,
    setShowDetection,
    showConsolidated,
    setShowConsolidated,
    showLabels,
    setShowLabels,
    showPromotions,
    setShowPromotions,
    showVertexNumbers,
    setShowVertexNumbers,
    sideView,
    setSideView,
    currencyFormat,
    setCurrencyFormat,
    selectedLabel,
    opacity,
    setOpacity,
    page,
    setPage,
    openMenu,
    setOpenMenu,
    setSelectedLabel,
    stage,
    handleStageChange,
    tabValue,
    handleTabChange,
    positionSource,
    setPositionSource,
    positionScope,
    setPositionScope,
    consolidationStagesOptions,
    getConsolidationStages,
    consolidationStages,
  } = props;
  const { inputBackground = 'white', Inputcolor } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [filteredChains, setFilteredChains] = useState(null);
  const [wallImgUrl, setWallImgUrl] = useState(null);
  const openConfig = Boolean(anchorEl);
  const lightBackground = inputBackground
    ? inputBackground
    : Object.values(theme.palette[inputBackground])[0];

  const switchColor = theme.palette.type === 'light' ? 'secondary' : 'primary';
  const hasStagesTab = READING_AI_TYPES.includes(formState.ia);
  const hasDisplayTab = formState.ia === 'Shelfout Detection';
  const activeTab =
    (tabValue === 'Stages' && !hasStagesTab) ||
    (tabValue === 'Display' && !hasDisplayTab)
      ? 'Options'
      : tabValue;

  const listAI = [
    {
      name: 'Bay Detection',
      types: ['Wall'],
      label: t('hip_app.general.bay_detection', 'Bay Detection'),
    },
    {
      name: 'Bay Label Detection',
      types: ['Shelf'],
      label: t('hip_app.general.bay_label_detection', 'Bay Label Detection'),
    },
    {
      name: 'Bay Label Reading',
      types: ['Shelf'],
      label: t('hip_app.general.bay_label_reading', 'Bay Label Reading'),
    },
    {
      name: 'Overhead Inventory Box Detection',
      types: ['Overhead'],
      label: t(
        'hip_app.general.overhead_inventory_box_detection',
        'Box Detection',
      ),
    },
    {
      name: 'Overhead Inventory Box Reading',
      types: ['Overhead'],
      label: t('hip_app.general.overhead_inventory_box_reading', 'Box Reading'),
    },
    {
      name: 'Consolidation',
      types: ['Wall'],
      label: t('hip_app.general.consolidation', 'Consolidation'),
    },
    {
      name: 'Label Detection',
      types: ['Overhead', 'Shelf'],
      label: t('hip_app.general.label_detection', 'Label Detection'),
    },
    {
      name: 'Label Reading',
      types: ['Overhead', 'Shelf'],
      label: t('hip_app.general.label_reading', 'Label Reading'),
    },
    {
      name: 'Product Detection',
      types: ['Shelf'],
      label: t('hip_app.general.product_detection', 'Product Detection'),
    },
    {
      name: 'Product Detection',
      types: ['Wall'],
      label: t('hip_app.general.product_detection', 'Product Detection'),
    },
    {
      name: 'Promotional Signage Detection',
      types: ['Shelf'],
      label: t(
        'hip_app.general.promotional_detection',
        'Promotional Signage Detection',
      ),
    },
    {
      name: 'Promotional Signage Reading',
      types: ['Shelf'],
      label: t(
        'hip_app.general.promotional_reading',
        'Promotional Signage Reading',
      ),
    },
    {
      name: 'Shelf Detection',
      types: ['Wall'],
      label: t('hip_app.general.shelf_detection', 'Shelf Detection'),
    },
    {
      name: 'Shelfout Detection',
      types: ['Wall'],
      label: t('hip_app.general.shelfout_detection', 'Shelfout Detection'),
    },
  ];

  const listType = [
    { name: 'Overhead', label: t('hip_app.general.overhead', 'Overhead') },
    { name: 'Shelf', label: t('hip_app.general.shelf', 'Shelf') },
    { name: 'Wall', label: t('hip_app.general.wall', 'Wall') },
  ];

  const listLimit = [
    { name: '100' },
    { name: '200' },
    { name: '500' },
    { name: '1000' },
    { name: '2000' },
    { name: '5000' },
    { name: '10000' },
  ];

  const listCollector = [
    { name: 'all', label: t('hip_app.general.all') },
    ...(props.sources ?? []).map(source => ({ name: source, label: source })),
  ];

  const listLayoutIds = (props.layoutIds ?? []).map(layoutId => ({
    name: layoutId,
    label: String(layoutId),
  }));

  const sortedAisles = props.aisles?.aisles ?? [];

  // Options are scoped to the same session, aisle and collector used to load images,
  // so every listed layout id has images. Overrides carry values not yet in formState.
  const fetchLayoutIds = (overrides = {}) => {
    const { store, session, type, ia, aisle, collector } = {
      ...formState,
      ...overrides,
    };
    if (session && type && LAYOUT_ID_AI_TYPES.includes(ia)) {
      props.getLayoutIds({
        store,
        session,
        objectType: type,
        aiType: ia,
        aisle,
        collector,
      });
    }
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseConfig = () => {
    setAnchorEl(null);
  };

  const handleShare = () => {
    const currentImage = props.images?.image?.[formState.index];
    const imageUrl = currentImage?.image_url || wallImgUrl;

    if (!imageUrl) {
      props.setSnackAlert({
        open: true,
        message: t('hip_app.general.no_image_found', 'No image found'),
        severity: 'warning',
      });
      return;
    }

    const currentState = {
      ...formState,
      image_url: imageUrl,
      ...currencyFormat,
      selectedLabel: selectedLabel,
      format: format,
      showDetection: showDetection,
      showVertexNumbers: showVertexNumbers,
      opacity: opacity,
      filteredChains: filteredChains,
      sideView: sideView,
      page: page,
    };
    const state = JSON.stringify(currentState);
    const encodedState = Buffer.from(state, 'utf8').toString('base64');
    const link = `${window.location.origin}/share?state=${encodedState}&view=visualizer`;
    navigator.clipboard.writeText(link);
    props.setSnackAlert({
      open: true,
      message: t('hip_app.general.share_link_alert'),
      severity: 'success',
    });
  };

  const handleShowAll = value => {
    setFormState({
      ...formState,
      showAll: value,
      index: 0,
    });
    setPage(1);
    props.cleanImages();
    props.cleanImage();
    props.getImages({
      store: formState.store,
      session: formState.session,
      object_type: formState.type,
      ia_type: formState.ia,
      capture_id: formState.capture,
      detection_id: formState.detection,
      filter: value,
      rows_limit: formState.limit,
      aisles: handleGetAisle(),
      layout_id: formState.layoutId,
    });
  };

  const handleGetAisle = (inputAisle = null) => {
    const targetAisleInfo = inputAisle ?? formState.aisle;
    return targetAisleInfo
      ? targetAisleInfo === 'All'
        ? []
        : [targetAisleInfo]
      : [];
  };

  const handleChange = (name, value, session) => {
    if (name === 'country') {
      if (formState.chain) {
        props.cleanStores();
        props.cleanImages();
        props.cleanImage();
      }
      setFormState({
        ...formState,
        store: null,
        aisle: 'All',
        session: null,
        ia: null,
        type: null,
        detection: '',
        capture: '',
        sku: '',
        consolidationStage: [],
        country: value,
        index: 0,
        chain: null,
        collector: 'all',
        layoutId: null,
      });
      setPage(1);
    } else if (name === 'chain') {
      if (formState.country) {
        props.cleanStores();
        props.cleanSessions();
        props.cleanImages();
        props.cleanImage();
        props.getStores({
          country_id: formState.country,
          chain_id: value,
        });
      }
      setFormState({
        ...formState,
        aisle: 'All',
        session: null,
        ia: null,
        type: null,
        detection: '',
        capture: '',
        sku: '',
        consolidationStage: [],
        store: null,
        chain: value,
        index: 0,
        collector: 'all',
        layoutId: null,
      });
      setPage(1);
    } else if (name === 'store') {
      props.cleanSessions();
      props.cleanImages();
      props.cleanImage();
      props.getSessions(value, REACT_APP_USE_BIFROST);
      setFormState({
        ...formState,
        store: value,
        aisle: 'All',
        session: null,
        detection: '',
        capture: '',
        sku: '',
        consolidationStage: [],
        index: 0,
        collector: 'all',
        layoutId: null,
      });
      setPage(1);
      const storeCurrency = props.stores?.response?.find(
        store => store.code === value,
      );
      if (storeCurrency) {
        setCurrencyFormat({
          currency_symbol: storeCurrency.currency_symbol,
          currency_thousands_sep: storeCurrency.currency_thousands_sep,
          currency_decimal_sep: storeCurrency.currency_decimal_sep,
          currency_decimals: storeCurrency.currency_decimals,
        });
      }
    } else if (name === 'session') {
      props.getAisles(value, formState.store, REACT_APP_USE_BIFROST);
      props.getSources(value, formState.type);
      fetchLayoutIds({ session: value, aisle: null, collector: 'all' });
      props.cleanImages();
      props.cleanImage();
      setFormState({
        ...formState,
        session: value,
        auxSession: value,
        detection: '',
        capture: '',
        sku: '',
        consolidationStage: [],
        aisle: null,
        index: 0,
        collector: 'all',
        layoutId: null,
      });
      setPage(1);
    } else if (name === 'aisle') {
      setFormState({
        ...formState,
        aisle: value,
        detection: '',
        capture: '',
        sku: '',
        consolidationStage: [],
        index: 0,
        layoutId: null,
      });
      setPage(1);
      fetchLayoutIds({ aisle: value });
      if (formState.type && formState.ia) {
        props.cleanImages();
        props.cleanImage();
        if (formState.ia === 'Consolidation') {
          getConsolidationStages({
            store: formState.store,
            session: formState.session,
            aisle: value,
          });
        } else {
          props.getImages({
            store: formState.store,
            session: formState.session,
            aisles: handleGetAisle(value),
            object_type: formState.type,
            ia_type: formState.ia,
            filter: formState.showAll,
            rows_limit: formState.limit,
            collector: formState.collector,
          });
        }
      }
    } else if (name === 'type') {
      props.cleanImages();
      props.getSources(formState.session, value);
      setFormState({
        ...formState,
        type: value,
        detection: '',
        capture: '',
        sku: '',
        consolidationStage: [],
        index: 0,
        ia: null,
        layoutId: null,
      });
      setPage(1);
    } else if (name === 'ia') {
      props.cleanImages();
      props.cleanImage();
      setFormState({
        ...formState,
        ia: value,
        detection: '',
        index: 0,
        capture: '',
        sku: '',
        consolidationStage: [],
        layoutId: null,
      });
      fetchLayoutIds({ ia: value });
      setPage(1);
      if (formState.aisle) {
        if (value === 'Consolidation') {
          getConsolidationStages({
            store: formState.store,
            session: formState.session,
            aisle: formState.aisle,
          });
        } else {
          props.getImages({
            store: formState.store,
            session: formState.session,
            object_type: formState.type,
            ia_type: value,
            filter: formState.showAll,
            rows_limit: formState.limit,
            collector: formState.collector,
            aisles: handleGetAisle(),
          });
        }
      }
    } else if (name === 'consolidationStage') {
      setFormState({
        ...formState,
        consolidationStage: value,
      });
    } else if (name === 'capture') {
      props.cleanImages();
      props.cleanImage();
      setFormState({
        ...formState,
        capture: value,
        index: 0,
        detection: '',
        sku: '',
      });
      setPage(1);
      props.getImages({
        store: formState.store,
        session: formState.session,
        object_type: formState.type,
        ia_type: formState.ia,
        capture_id: value,
        filter: false,
        rows_limit: formState.limit,
        collector: formState.collector,
        layout_id: formState.layoutId,
        aisles: handleGetAisle(),
      });
    } else if (name === 'detection') {
      props.cleanImages();
      props.cleanImage();
      setFormState({
        ...formState,
        detection: value,
        index: 0,
        aisle: formState.aisle,
        capture: '',
        ia: formState.ia,
        auxSession: session ? session : formState.session,
      });
      setPage(1);
      props.getImages({
        store: formState.store,
        session: session ? session : formState.session,
        object_type: formState.type,
        ia_type: formState.ia,
        detection_id: value,
        filter: false,
        rows_limit: formState.limit,
        collector: formState.collector,
        layout_id: formState.layoutId,
        aisles: handleGetAisle(),
      });
    } else if (name === 'limit') {
      props.cleanImages();
      props.cleanImage();
      setFormState({
        ...formState,
        limit: value,
        index: 0,
      });
      setPage(1);
      if (formState.ia) {
        props.getImages({
          store: formState.store,
          session: formState.session,
          object_type: formState.type,
          ia_type: formState.ia,
          capture_id: formState.capture,
          detection_id: formState.detection,
          filter: formState.showAll,
          rows_limit: value,
          aisles: handleGetAisle(),
          collector: formState.collector,
          layout_id: formState.layoutId,
        });
      }
    } else if (name === 'sku') {
      props.cleanImages();
      props.cleanImage();
      props.clearDetections();

      if (value) {
        setFormState({
          ...formState,
          sku: value,
          index: 0,
          detection: null,
          capture: null,
        });
        setPage(1);
        props.getDetectionsByValue({
          store: formState.store,
          value,
          object_type: formState.type,
          ia_type: formState.ia,
          aisles: handleGetAisle(),
          rows_limit: formState.limit,
          session: formState.session,
        });
      } else {
        setFormState({
          ...formState,
          sku: value,
          index: 0,
          detection: '',
          capture: '',
        });
        setPage(1);
        props.getImages({
          store: formState.store,
          session: formState.session,
          object_type: formState.type,
          ia_type: formState.ia,
          filter: formState.showAll,
          rows_limit: formState.limit,
          aisles: handleGetAisle(),
          collector: formState.collector,
          layout_id: formState.layoutId,
        });
      }
    } else if (name === 'collector') {
      props.cleanImages();
      props.cleanImage();
      setFormState({
        ...formState,
        index: 0,
        collector: value,
        layoutId: null,
      });
      setPage(1);
      fetchLayoutIds({ collector: value });

      if (formState.ia) {
        props.getImages({
          store: formState.store,
          session: formState.session,
          object_type: formState.type,
          ia_type: formState.ia,
          capture_id: formState.capture,
          detection_id: formState.detection,
          filter: formState.showAll,
          rows_limit: formState.limit,
          aisles: handleGetAisle(),
          collector: value,
        });
      }
    } else if (name === 'layoutId') {
      props.cleanImages();
      props.cleanImage();
      setFormState({
        ...formState,
        index: 0,
        layoutId: value,
      });
      setPage(1);

      if (formState.ia) {
        props.getImages({
          store: formState.store,
          session: formState.session,
          object_type: formState.type,
          ia_type: formState.ia,
          capture_id: formState.capture,
          detection_id: formState.detection,
          filter: formState.showAll,
          rows_limit: formState.limit,
          aisles: handleGetAisle(),
          collector: formState.collector,
          layout_id: value,
        });
      }
    }
  };

  useEffect(() => {
    if (consolidationStages?.image?.[0]?.image_url) {
      setWallImgUrl(consolidationStages.image[0].image_url);
      props.signImage({
        store: formState.store,
        blob_path: consolidationStages.image[0].image_url,
      });
    }
  }, [consolidationStages]);

  useEffect(() => {
    const { countries, chains } = props;
    const isConsolidation = formState.ia === 'Consolidation';

    if (!countries || !chains) {
      props.getCountries();
      props.getChains();
    }

    if (
      !isConsolidation &&
      props.images &&
      props.images.image &&
      formState.index === 0 &&
      props.images.image[formState.index]
    ) {
      props.cleanImage();
      props.signImage({
        store: formState.store,
        blob_path: props.images.image[formState.index].image_url,
      });
    }

    if (
      props.sessions &&
      props.sessions.sessions &&
      formState.session === null &&
      props.sessions.sessions[0] &&
      props.sessions.sessions[0].includes(formState.store) &&
      formState.country &&
      formState.chain
    ) {
      handleChange('session', props.sessions.sessions[0]);
    }

    if (
      props.detections &&
      props.detections[0] &&
      props.detections[0].detection_id &&
      formState.detection === null
    ) {
      handleChange(
        'detection',
        props.detections[0].detection_id,
        props.detections[0].session,
      );
    }

    if (
      props.recievedState &&
      !formState.country &&
      !formState.chain &&
      !formState.store
    ) {
      const state = props.recievedState;

      props.getStores({
        country_id: state.country,
        chain_id: state.chain,
      });
      props.getSessions(state.store, REACT_APP_USE_BIFROST);
      props.getAisles(state.session, state.store, REACT_APP_USE_BIFROST);
      props.getSources(state.session, state.type);
      if (LAYOUT_ID_AI_TYPES.includes(state.ia)) {
        props.getLayoutIds({
          store: state.store,
          session: state.session,
          objectType: state.type,
          aiType: state.ia,
          aisle: state.aisle,
          collector: state.collector,
        });
      }

      if (state.sku) {
        props.getDetectionsByValue({
          store: state.store,
          value: state.sku,
          object_type: state.type,
          ia_type: state.ia,
          aisles: handleGetAisle(),
          rows_limit: state.limit,
        });
      }

      setFormState({
        country: state.country,
        chain: state.chain,
        store: state.store,
        consolidationStage: state.consolidationStage || [],
        aisle: state.aisle,
        session: state.session,
        ia: state.ia,
        detection: state.detection,
        capture: state.capture,
        type: state.type,
        index: state.index,
        showAll: state.showAll,
        limit: state.limit,
        sku: state.sku,
        auxSession: state.session,
        collector: state.collector,
        layoutId: state.layoutId ?? null,
      });

      if (state.ia === 'Consolidation') {
        getConsolidationStages({
          store: state.store,
          session: state.session,
          aisle: state.aisle,
        });
      } else {
        props.getImages({
          store: state.store,
          session: state.auxSession,
          object_type: state.type,
          ia_type: state.ia,
          capture_id: state.capture,
          detection_id: state.detection,
          filter: state.showAll,
          rows_limit: state.limit,
          aisles: handleGetAisle(state.aisle),
          layout_id: state.layoutId ?? null,
        });
      }

      props.signImage({
        store: state.store,
        blob_path: state.image_url,
      });

      setCurrencyFormat({
        currency_symbol: state.currency_symbol,
        currency_thousands_sep: state.currency_thousands_sep,
        currency_decimal_sep: state.currency_decimal_sep,
        currency_decimals: state.currency_decimals,
      });

      setFormat(state.format);
      setSelectedLabel(state.selectedLabel);
      setShowDetection(state.showDetection);
      setShowVertexNumbers(state.showVertexNumbers ?? false);
      setOpacity(state.opacity);
      setFilteredChains(state.filteredChains);
      setSideView(state.sideView);
      setPage(state.page);
    }
  }, [formState, props.images, props.sessions, props.detections]);

  useEffect(() => {
    const countries = props.countries;
    const chains = props.chains;

    if (countries?.response && chains?.response && formState.country) {
      const selectedCountry = countries.response.find(
        country => country.country_id === formState.country,
      );

      if (!selectedCountry) {
        setFilteredChains([]);
        return;
      }

      const nextFilteredChains = chains.response.filter(
        chain => chain.country_name === selectedCountry.name,
      );
      setFilteredChains(nextFilteredChains);
    }
  }, [formState.country, props.countries, props.chains]);

  return (
    <Paper
      component={Grid}
      container
      p={2}
      sx={{
        backgroundColor:
          theme.palette.type === 'light'
            ? lightBackground
            : theme.palette.paper.main,
      }}>
      {/* Settings menu */}
      <StyledMenu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openConfig}
        onClose={handleCloseConfig}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}>
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList
              onChange={handleTabChange}
              variant="fullWidth"
              textColor="inherit">
              <Tab label="Options" value="Options" />
              {hasStagesTab ? <Tab label="Stages" value="Stages" /> : null}
              {hasDisplayTab ? <Tab label="Display" value="Display" /> : null}
            </TabList>
          </Box>
          <TabPanel value="Options">
            {SHELF_READING_AI_TYPES.includes(formState.ia) ? (
              <MenuItem onClick={handleCloseConfig}>
                <FormControlLabel
                  control={
                    <Switch
                      color={
                        theme.palette.type === 'light' ? 'secondary' : 'primary'
                      }
                      size="small"
                      checked={format}
                      onChange={e => setFormat(e.target.checked ? true : false)}
                    />
                  }
                  label={t('hip_app.general.format_currency')}
                  labelPlacement="end"
                />
              </MenuItem>
            ) : null}

            {formState.ia ? (
              <>
                {formState.ia === 'Product Detection' ? (
                  <MenuItem onClick={handleCloseConfig}>
                    <FormControlLabel
                      control={
                        <Switch
                          color={
                            theme.palette.type === 'light'
                              ? 'secondary'
                              : 'primary'
                          }
                          size="small"
                          checked={showConsolidated}
                          onChange={e =>
                            setShowConsolidated(e.target.checked ? true : false)
                          }
                        />
                      }
                      label={t(
                        'hip_app.general.show_not_consolidation_boxes',
                        'Show not consolidated boxes',
                      )}
                      labelPlacement="end"
                    />
                  </MenuItem>
                ) : null}

                <MenuItem onClick={handleCloseConfig}>
                  <FormControlLabel
                    control={
                      <Switch
                        color={
                          theme.palette.type === 'light'
                            ? 'secondary'
                            : 'primary'
                        }
                        size="small"
                        checked={showDetection}
                        onChange={e =>
                          setShowDetection(e.target.checked ? true : false)
                        }
                      />
                    }
                    label={t('hip_app.general.show_bounding_boxes')}
                    labelPlacement="end"
                  />
                </MenuItem>
                <MenuItem onClick={handleCloseConfig}>
                  <FormControlLabel
                    control={
                      <Switch
                        color={
                          theme.palette.type === 'light'
                            ? 'secondary'
                            : 'primary'
                        }
                        size="small"
                        checked={formState.showAll}
                        onChange={e => handleShowAll(e.target.checked)}
                      />
                    }
                    label={t('hip_app.general.show_all')}
                    labelPlacement="end"
                  />
                </MenuItem>
                <MenuItem onClick={handleCloseConfig}>
                  <FormControlLabel
                    control={
                      <Switch
                        color={
                          theme.palette.type === 'light'
                            ? 'secondary'
                            : 'primary'
                        }
                        size="small"
                        checked={showLabels}
                        onChange={e => setShowLabels(e.target.checked)}
                      />
                    }
                    label={'Show labels'}
                    labelPlacement="end"
                  />
                </MenuItem>
                <MenuItem onClick={handleCloseConfig}>
                  <FormControlLabel
                    control={
                      <Switch
                        color={
                          theme.palette.type === 'light'
                            ? 'secondary'
                            : 'primary'
                        }
                        size="small"
                        checked={showPromotions}
                        onChange={e => setShowPromotions(e.target.checked)}
                      />
                    }
                    label={'Show promotions'}
                    labelPlacement="end"
                  />
                </MenuItem>
                <MenuItem onClick={handleCloseConfig}>
                  <FormControlLabel
                    control={
                      <Switch
                        color={
                          theme.palette.type === 'light'
                            ? 'secondary'
                            : 'primary'
                        }
                        size="small"
                        checked={showVertexNumbers}
                        onChange={e => setShowVertexNumbers(e.target.checked)}
                      />
                    }
                    label={'Show vertex numbers'}
                    labelPlacement="end"
                  />
                </MenuItem>
              </>
            ) : null}
            <MenuItem onClick={handleCloseConfig}>
              <FormControlLabel
                control={
                  <Switch
                    color={
                      theme.palette.type === 'light' ? 'secondary' : 'primary'
                    }
                    size="small"
                    checked={sideView}
                    onChange={e => setSideView(e.target.checked ? true : false)}
                  />
                }
                label={t('hip_app.general.side_by_side')}
                labelPlacement="end"
              />
            </MenuItem>
            <MenuItem>
              <Typography gutterBottom>
                {t('hip_app.general.opacity')}
              </Typography>
              <StyledSlider
                size="small"
                aria-label={t('hip_app.general.opacity')}
                value={opacity}
                valueLabelFormat={value => `${value * 100}%`}
                step={0.001}
                min={0}
                max={1}
                onChange={e => setOpacity(e.target.value)}
                sx={{ maxWidth: '70%', marginLeft: '1rem' }}
              />
            </MenuItem>
          </TabPanel>
          <TabPanel value="Stages">
            <FormControl>
              <RadioGroup
                aria-labelledby="stage-buttons"
                name="controlled-stage-buttons"
                value={stage}
                onChange={handleStageChange}>
                <FormControlLabel
                  value="text_recognition"
                  control={<Radio />}
                  label="First Stage Raw"
                />
                <FormControlLabel
                  value="text_recognition_postprocessed"
                  control={<Radio />}
                  label="First Stage Post Processed"
                />
                <FormControlLabel
                  value="results"
                  control={<Radio />}
                  label="First Stage Results"
                />
                <FormControlLabel
                  value="second_stage"
                  control={<Radio />}
                  label="Second Stage"
                />
              </RadioGroup>
            </FormControl>
          </TabPanel>
          <TabPanel value="Display">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('hip_app.general.position_source', 'Position source')}
                </Typography>
                <OptionSwitch
                  color={switchColor}
                  value={positionSource}
                  onChange={setPositionSource}
                  leftOption={{
                    value: SHELFOUT_POSITION_SOURCES.ALGORITHM,
                    label: t(
                      'hip_app.general.position_algorithm',
                      'Position Algorithm',
                    ),
                  }}
                  rightOption={{
                    value: SHELFOUT_POSITION_SOURCES.PREDICTION,
                    label: t(
                      'hip_app.general.position_prediction',
                      'Position Prediction',
                    ),
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('hip_app.general.show_positions', 'Show')}
                </Typography>
                <OptionSwitch
                  color={switchColor}
                  value={positionScope}
                  onChange={setPositionScope}
                  leftOption={{
                    value: SHELFOUT_POSITION_SCOPES.SHELFOUTS,
                    label: t(
                      'hip_app.general.shelfouts_only',
                      'Shelfouts only',
                    ),
                  }}
                  rightOption={{
                    value: SHELFOUT_POSITION_SCOPES.ALL,
                    label: t('hip_app.general.all_positions', 'All positions'),
                  }}
                />
              </Box>
            </Box>
          </TabPanel>
        </TabContext>
        <Divider
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.23)',
          }}
        />
        <MenuItem
          onClick={() => {
            handleShare();
            handleCloseConfig();
          }}>
          <IconButton>
            <IconComponent
              className={'icon'}
              iconName={'share-social-outline'}
              style={{
                color: 'white',
                height: '2.3vh',
              }}
            />
          </IconButton>
          <Typography>{t('hip_app.general.share')}</Typography>
        </MenuItem>
      </StyledMenu>
      {/* Selectors */}
      <Grid container item xs={12} sm={11}>
        <Selector
          required
          disableClearable
          setSelectedElement={handleChange}
          name={'country'}
          labelInput={t('hip_app.general.country')}
          options={props.countries ? props.countries.response : []}
          valueLabel={'country_id'}
          selectedElement={formState.country}
          optionLabel={'name'}
          disabled={props.isLoadingImages}
          isLoading={props.isLoadingCountries}
          sx={{ flexGrow: 1 }}
        />
        <Selector
          required
          disableClearable
          setSelectedElement={handleChange}
          name={'chain'}
          labelInput={t('hip_app.general.chain')}
          options={filteredChains ? filteredChains : []}
          valueLabel={'chain_id'}
          selectedElement={formState.chain}
          optionLabel={'name'}
          disabled={!filteredChains || props.isLoadingImages}
          isLoading={props.isLoadingChains}
          sx={{ flexGrow: 1 }}
        />
        <Selector
          required
          disableClearable
          setSelectedElement={handleChange}
          name={'store'}
          labelInput={t('hip_app.general.store')}
          options={props.stores ? props.stores.response : []}
          valueLabel={'code'}
          selectedElement={formState.store}
          optionLabel={'name'}
          disabled={!props.stores || props.isLoadingImages}
          isLoading={props.isLoadingStores}
          sx={{ flexGrow: 1 }}
        />
        <Selector
          required
          disableClearable
          setSelectedElement={handleChange}
          name={'session'}
          labelInput={t('hip_app.general.session')}
          options={props.sessions ? props.sessions.sessions : []}
          valueLabel={'session'}
          selectedElement={formState.session}
          disabled={
            !props.sessions || !formState.store || props.isLoadingImages
          }
          defaultValue={formState.session ? true : false}
          isLoading={props.isLoadingSessions}
          sx={{ flexGrow: 1 }}
        />
        <Selector
          required
          disableClearable
          setSelectedElement={handleChange}
          name={'aisle'}
          labelInput={t('hip_app.general.aisle')}
          options={
            props.aisles
              ? [{ aisle: 'All', nav_finished: true }, ...sortedAisles]
              : [{ aisle: 'All', nav_finished: true }]
          }
          valueLabel={'aisle'}
          selectedElement={formState.aisle}
          optionLabel={'aisle'}
          disabled={
            !props.aisles ||
            !formState.session ||
            props.isLoadingImages ||
            props.isLoadingAisles
          }
          isLoading={props.isLoadingAisles}
          defaultValue={formState.aisle ? true : false}
          sx={{ flexGrow: 1 }}
        />
        <Selector
          required
          disableClearable
          setSelectedElement={handleChange}
          name={'type'}
          labelInput={t('hip_app.general.type')}
          options={listType}
          valueLabel={'name'}
          selectedElement={formState.type}
          optionLabel={'label'}
          disabled={
            !formState.limit ||
            !formState.session ||
            props.isLoadingImages ||
            !formState.session
          }
          sx={{ flexGrow: 1 }}
        />
        <Selector
          required
          disableClearable
          setSelectedElement={handleChange}
          name={'ia'}
          labelInput={t('hip_app.general.ai')}
          options={listAI.filter(item => item.types.includes(formState.type))}
          valueLabel={'name'}
          selectedElement={formState.ia}
          optionLabel={'label'}
          disabled={
            !formState.type || props.isLoadingImages || !formState.session
          }
          sx={{ flexGrow: 1 }}
        />
        {formState.ia === 'Consolidation' && (
          <Selector
            required
            disableClearable
            multiple
            setSelectedElement={handleChange}
            name={'consolidationStage'}
            labelInput={t('hip_app.general.consolidation_stage')}
            options={consolidationStagesOptions || []}
            valueLabel={'name'}
            selectedElement={formState.consolidationStage}
            optionLabel={'label'}
            disabled={
              props.isLoadingConsolidationStages || !consolidationStages
            }
            isLoading={props.isLoadingConsolidationStages}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.label}
                  style={{ backgroundColor: option.color, color: 'white' }}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                  sx={{
                    color: option.color,
                    '&.Mui-checked': {
                      color: option.color,
                    },
                  }}
                />
                {option.label}
              </li>
            )}
            sx={{ flexGrow: 1, minWidth: '12rem' }}
          />
        )}
      </Grid>
      <Grid
        container
        item
        xs={12}
        sm={1}
        justifyContent="flex-end"
        alignContent="center">
        {/* Collapse button */}
        <IconButton onClick={() => setOpenMenu(!openMenu)}>
          <IconComponent
            iconName={openMenu ? 'chevron-up-outline' : 'chevron-down-outline'}
            style={{
              color:
                theme.palette.type === 'dark'
                  ? 'white'
                  : theme.palette.primary.main,
              height: '2.3vh',
            }}
          />
        </IconButton>
        {/* Settings button */}
        <IconButton onClick={handleClick}>
          <IconComponent
            iconName={'settings-outline'}
            style={{
              color:
                theme.palette.type === 'dark'
                  ? 'white'
                  : theme.palette.primary.main,
              height: '2.3vh',
            }}
          />
        </IconButton>
      </Grid>
      {/* Collapsable selectors and inputs */}
      <Collapse in={openMenu} style={{ width: '100%' }}>
        <Grid container item xs={12} sm={11}>
          <Selector
            required
            disableClearable
            setSelectedElement={handleChange}
            name={'limit'}
            labelInput={t('hip_app.general.image_limit')}
            options={listLimit}
            valueLabel={'name'}
            selectedElement={formState.limit}
            optionLabel={'name'}
            disabled={!formState.session || props.isLoadingImages}
            sx={{ flexGrow: 1 }}
          />
          {formState.type === 'Shelf' &&
          COLLECTOR_AI_TYPES.includes(formState.ia) ? (
            <Selector
              disableClearable
              name={'collector'}
              labelInput={t('hip_app.general.collector')}
              valueLabel={'name'}
              selectedElement={formState.collector}
              setSelectedElement={handleChange}
              options={listCollector}
              optionLabel={'label'}
              disabled={props.isLoadingImages || props.isLoadingSources}
              isLoading={props.isLoadingSources}
              sx={{ flexGrow: 1 }}
            />
          ) : null}
          {formState.session && LAYOUT_ID_AI_TYPES.includes(formState.ia) ? (
            <Selector
              name={'layoutId'}
              labelInput={t('hip_app.general.layout_id', 'Layout ID')}
              valueLabel={'name'}
              selectedElement={formState.layoutId}
              setSelectedElement={handleChange}
              options={listLayoutIds}
              optionLabel={'label'}
              disabled={props.isLoadingImages || props.isLoadingLayoutIds}
              isLoading={props.isLoadingLayoutIds}
              sx={{ flexGrow: 1 }}
            />
          ) : null}
          {props.stores &&
          props.sessions &&
          formState.ia &&
          READING_AI_TYPES.includes(formState.ia) ? (
            <Input
              name={'sku'}
              labelInput={'SKU'}
              selectedElement={formState.sku}
              setSelectedElement={handleChange}
              disabled={props.isLoadingImages}
              sx={{ flexGrow: 1 }}
            />
          ) : null}
          {props.stores &&
          props.sessions &&
          formState.ia &&
          formState.ia !== 'Shelfout Detection' ? (
            !formState.sku ? (
              <Input
                name={
                  READING_AI_TYPES.includes(formState.ia)
                    ? 'detection'
                    : 'capture'
                }
                labelInput={
                  READING_AI_TYPES.includes(formState.ia)
                    ? 'Detection ID'
                    : 'Capture ID'
                }
                selectedElement={
                  READING_AI_TYPES.includes(formState.ia)
                    ? formState.detection
                    : formState.capture
                }
                setSelectedElement={handleChange}
                disabled={
                  props.isLoadingImages ||
                  !formState.type ||
                  !formState.ia ||
                  formState.type === 'Wall'
                }
                sx={{ flexGrow: 1 }}
              />
            ) : (
              <Selector
                disableClearable
                name={'detection'}
                labelInput={`Detection ID ${
                  props.detections ? '(' + props.detections.length + ')' : ''
                }`}
                valueLabel={'detection_id'}
                secondaryValueLabel="session"
                selectedElement={formState.detection}
                setSelectedElement={handleChange}
                options={props.detections ? props.detections : []}
                optionLabel="detection_id"
                groupLabel="session"
                isLoading={props.isLoadingDetections}
                disabled={props.isLoadingImages}
                sx={{ flexGrow: 1 }}
              />
            )
          ) : null}
        </Grid>
      </Collapse>
    </Paper>
  );
}
